import { useCallback, useEffect, useRef, useState } from "react";

interface FullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
}

interface FullscreenDocument {
  webkitFullscreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
  msExitFullscreen?: () => Promise<void>;
}

type FullscreenHookReturn = {
  isAvailable: boolean;
  isFullscreen: boolean;
  enter: () => Promise<void>;
  exit: () => Promise<void>;
  toggle: () => Promise<void>;
};

function getFullscreenElement(): Element | null {
  const doc = document as unknown as FullscreenDocument;
  return (
    document.fullscreenElement ??
    doc.webkitFullscreenElement ??
    doc.msFullscreenElement ??
    null
  );
}

function isFullscreenApiAvailable(): boolean {
  const el = document.createElement("div") as FullscreenElement;
  return !!(
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.msRequestFullscreen
  );
}

export function useFullscreen(
  targetRef: React.RefObject<HTMLElement | null>,
): FullscreenHookReturn {
  const [isAvailable] = useState(() => isFullscreenApiAvailable());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isFullscreenRef = useRef(isFullscreen);

  useEffect(() => {
    isFullscreenRef.current = isFullscreen;
  }, [isFullscreen]);

  const updateState = useCallback(() => {
    const current = getFullscreenElement();
    const target = targetRef.current;
    setIsFullscreen(!!target && current === target);
  }, [targetRef]);

  const enter = useCallback(async () => {
    const target = targetRef.current;
    if (!target || !isAvailable) return;

    const el = target as FullscreenElement;
    try {
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        await el.webkitRequestFullscreen();
      } else if (el.msRequestFullscreen) {
        await el.msRequestFullscreen();
      }
    } catch {
      // Fullscreen may be rejected by the browser (e.g. no user gesture).
    }
  }, [targetRef, isAvailable]);

  const exit = useCallback(async () => {
    if (!isAvailable || !getFullscreenElement()) return;

    const doc = document as unknown as FullscreenDocument;
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        await doc.webkitExitFullscreen();
      } else if (doc.msExitFullscreen) {
        await doc.msExitFullscreen();
      }
    } catch {
      // Exit may be rejected; leave state to event handlers.
    }
  }, [isAvailable]);

  const toggle = useCallback(async () => {
    if (isFullscreenRef.current) {
      await exit();
    } else {
      await enter();
    }
  }, [enter, exit]);

  useEffect(() => {
    if (!isAvailable) return;

    const events = ["fullscreenchange", "webkitfullscreenchange", "msfullscreenchange"];
    events.forEach((event) => document.addEventListener(event, updateState));
    updateState();

    return () => {
      events.forEach((event) => document.removeEventListener(event, updateState));
    };
  }, [isAvailable, updateState]);

  return { isAvailable, isFullscreen, enter, exit, toggle };
}
